
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Layout } from "@/components/layout/Layout";
import { PlusCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { SEO } from "@/components/seo/SEO";
import { fetchBusinessListings } from "@/services/businessService";
import { BusinessTabContent } from "@/components/business/BusinessTabContent";
import { KoreanBusinessTabContent } from "@/components/business/KoreanBusinessTabContent";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";

const BusinessTutoring = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  
  const { data: businesses, isLoading, error } = useQuery({
    queryKey: ['businesses'],
    queryFn: fetchBusinessListings
  });
  
  const handleBusinessClick = (id: string) => {
    navigate(`/business-tutoring/business/${id}`);
  };
  
  // Function to check if user can create business listings - only allow ilwoonge@gmail.com
  const canCreateBusinessListing = () => {
    return user !== null && user.email === 'ilwoonge@gmail.com';
  };
  
  return (
    <Layout>
      <SEO title="한인레슨/업체" description="라스베가스 지역 한인레슨, 업체, 오픈톡방 목록" />
      
      <div className="container py-8 max-w-screen-lg">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">한인레슨/업체</h1>
            <p className="text-muted-foreground">라스베가스 지역 한인레슨, 업체, 오픈톡방을 찾아보세요.</p>
          </div>
          {canCreateBusinessListing() && (
            <Button onClick={() => navigate('/business-tutoring/write')}>
              <PlusCircle className="mr-2 h-4 w-4" />
              새 글 등록
            </Button>
          )}
        </div>
        
        <Tabs defaultValue="businesses" className="mb-8">
          <TabsList className={isMobile ? "flex flex-wrap h-auto" : ""}>
            <TabsTrigger value="businesses" className={isMobile ? "flex-1 min-w-[33%]" : ""}>과외/학원</TabsTrigger>
            <TabsTrigger value="koreanBusiness" className={isMobile ? "flex-1 min-w-[33%]" : ""}>한인업체</TabsTrigger>
            <TabsTrigger value="koreanMart" className={isMobile ? "flex-1 min-w-[33%]" : ""}>한인마트</TabsTrigger>
            <TabsTrigger value="realEstate" className={isMobile ? "flex-1 min-w-[33%]" : ""}>부동산</TabsTrigger>
            <TabsTrigger value="attorney" className={isMobile ? "flex-1 min-w-[33%]" : ""}>변호사</TabsTrigger>
            <TabsTrigger value="openChat" className={isMobile ? "flex-1 min-w-[33%]" : ""}>오픈톡방</TabsTrigger>
          </TabsList>
          
          <TabsContent value="businesses" className="mt-6">
            <BusinessTabContent 
              businesses={businesses}
              isLoading={isLoading}
              error={error}
              category="businesses"
              handleBusinessClick={handleBusinessClick}
            />
          </TabsContent>
          
          <TabsContent value="koreanBusiness" className="mt-6">
            <KoreanBusinessTabContent 
              businesses={businesses}
              isLoading={isLoading}
              handleBusinessClick={handleBusinessClick}
            />
          </TabsContent>
          
          <TabsContent value="koreanMart" className="mt-6">
            <BusinessTabContent 
              businesses={businesses}
              isLoading={isLoading}
              error={error}
              category="koreanMart"
              handleBusinessClick={handleBusinessClick}
            />
          </TabsContent>
          
          <TabsContent value="realEstate" className="mt-6">
            <BusinessTabContent 
              businesses={businesses}
              isLoading={isLoading}
              error={error}
              category="realEstate"
              handleBusinessClick={handleBusinessClick}
            />
          </TabsContent>
          
          <TabsContent value="attorney" className="mt-6">
            <BusinessTabContent 
              businesses={businesses}
              isLoading={isLoading}
              error={error}
              category="attorney"
              handleBusinessClick={handleBusinessClick}
            />
          </TabsContent>
          
          <TabsContent value="openChat" className="mt-6">
            <BusinessTabContent 
              businesses={businesses}
              isLoading={isLoading}
              error={error}
              category="openChat"
              handleBusinessClick={handleBusinessClick}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default BusinessTutoring;
